// Transparency page — Req 21. Spells out exactly what data Link
// stores and what it doesn't, retention windows, and how we handle
// law-enforcement requests. Versioned changelog at the bottom so
// changes to the policy are auditable.

import { LegalLayout } from "./LegalLayout";

export function TransparencyPage() {
  return (
    <LegalLayout
      title="What we store, and what we don't"
      revisedAt="May 24, 2026"
      current="transparency"
    >
      <p>
        Link is built around the smallest amount of data we can
        plausibly run a chat-and-workspace product with. This page
        walks through every category — what we keep, what we never
        collect, how long we keep it, and how we respond when
        someone asks for it.
      </p>

      <h2>Stored on our servers</h2>
      <ul>
        <li>
          <strong>Account hash + public key.</strong> Your account
          ID is the SHA-256 of your public key, base32-encoded. The
          public key is what we sign challenges against; the private
          key never leaves your device.
        </li>
        <li>
          <strong>Display name and avatar URL.</strong> Whatever
          you set in Settings → Profile.
        </li>
        <li>
          <strong>Spaces, channels, members, roles, invites.</strong>
          {" "}
          Structural metadata for the rooms you and your friends
          are in.
        </li>
        <li>
          <strong>Messages in shared workspaces and communities.</strong>
          {" "}
          Encrypted at rest with a per-Space key managed by us.
          Indexed for search inside that Space.
        </li>
        <li>
          <strong>Ciphertext envelopes for E2E channels.</strong>
          {" "}
          Personal Workspaces, DMs, and Secrets channels are
          end-to-end encrypted. We hold the bytes; we cannot read
          them.
        </li>
        <li>
          <strong>Attachments.</strong> Object storage with a
          25 MB cap per file. Encrypted at rest in shared
          workspaces and communities.
        </li>
      </ul>

      <h2>What we never collect</h2>
      <ul>
        <li>Your recovery phrase, seed, or private key.</li>
        <li>Your email address, phone number, or any third-party login.</li>
        <li>The plaintext of your Personal Workspace, your DMs, or any Secrets channel.</li>
        <li>Your AI prompts or assistant responses — those go straight from your browser to the provider you picked.</li>
      </ul>

      <h2>Retention</h2>
      <ul>
        <li><strong>Sessions.</strong> Until you log out or rotate keys.</li>
        <li><strong>Session IP address.</strong> Discarded 24 hours after the session ends.</li>
        <li><strong>Realtime event log.</strong> 7 days, for resync after disconnect.</li>
        <li><strong>Deleted messages.</strong> Tombstoned immediately; ciphertext + index entry purged within 24 hours.</li>
        <li><strong>Secrets channel messages.</strong> Default 30 days; user-selectable from 24 hours up to never. Purged within an hour of expiration.</li>
        <li><strong>Account deletion.</strong> 7-day grace window in case it was a mistake. After that we keep only a tombstone of the account ID hash.</li>
      </ul>

      <h2>Law enforcement requests</h2>
      <p>
        We respond to valid legal process. Because of the design,
        the surface we can produce is limited: account hash, public
        key, account creation date, last session IP if within the
        24-hour window, and shared-workspace / community message
        metadata. We cannot produce E2E plaintext — Personal
        Workspace, DM, or Secrets content — and we will not retain
        data beyond the windows above to facilitate a request.
      </p>

      <h2>Changelog</h2>
      <ul>
        <li><strong>v1.0 · May 24, 2026.</strong> Initial Link v1 launch.</li>
      </ul>
    </LegalLayout>
  );
}
